import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Shield, Scale, Flame, ArrowRight, Zap, Check, RefreshCw, Bot } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROFILES_DATA = [
  {
    key: 'conservative',
    nameKey: 'onboarding.conservative',
    subtitleKey: 'onboarding.capitalPreservation',
    icon: Shield,
    color: 'blue',
    border: 'border-blue-500',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    btnBg: 'bg-blue-500',
    agents: 2,
    instruments: 17,
    targets: ['AOK', 'GOVT', 'AGG', 'BND', 'TLT', 'GLD', 'SPY'],
    allocKey: 'onboarding.conservativeAlloc',
    descKey: 'onboarding.conservativeDesc',
    capitalPerAgent: '$50,000',
  },
  {
    key: 'moderate',
    nameKey: 'onboarding.moderate',
    subtitleKey: 'onboarding.balancedGrowth',
    icon: Scale,
    color: 'amber',
    border: 'border-amber-500',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    btnBg: 'bg-amber-500',
    agents: 6,
    instruments: 21,
    targets: ['AOR', 'VTI', 'INCM', 'SPY', 'QQQ', 'BTC', 'EUR/USD'],
    allocKey: 'onboarding.moderateAlloc',
    descKey: 'onboarding.moderateDesc',
    capitalPerAgent: '$25,000',
  },
  {
    key: 'aggressive',
    nameKey: 'onboarding.aggressive',
    subtitleKey: 'onboarding.maximumAlpha',
    icon: Flame,
    color: 'red',
    border: 'border-red-500',
    bg: 'bg-red-500/10',
    text: 'text-red-400',
    btnBg: 'bg-red-500',
    agents: 7,
    instruments: 28,
    targets: ['BTC', 'ETH', 'SOL', 'TQQQ', 'ARKK', 'AOA', 'NVDA', 'IP Assets'],
    allocKey: 'onboarding.aggressiveAlloc',
    descKey: 'onboarding.aggressiveDesc',
    capitalPerAgent: '$15,000',
  },
];

export default function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  const PROFILES = PROFILES_DATA.map(p => ({
    ...p,
    name: t(p.nameKey),
    subtitle: t(p.subtitleKey),
    desc: t(p.descKey),
    allocation: t(p.allocKey),
  }));

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/onboarding`,
        { risk_profile: selected },
        { withCredentials: true }
      );
      setResult(data);
      setDone(true);
      await checkAuth();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  if (done && result) {
    const profile = PROFILES.find(p => p.key === selected);
    const Icon = profile.icon;

    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center px-4" data-testid="onboarding-complete">
        <div className="max-w-md w-full text-center space-y-6">
          <div className={`w-16 h-16 ${profile.bg} flex items-center justify-center mx-auto`}>
            <Check className={`w-8 h-8 ${profile.text}`} />
          </div>

          <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('onboarding.allSet')}
          </h1>

          <p className="text-sm text-slate-400">
            {t('onboarding.profileActive', { profile: profile.name, amount: result.total_allocated?.toLocaleString(), count: result.agents_allocated?.length }).split('<1>')[0]}
            <span className={`font-bold ${profile.text}`}>{profile.name}</span>
            {' '}{t('onboarding.profileActive').split('</1>')[1]?.split('<2>')[0]}
            <span className="text-white font-semibold">${result.total_allocated?.toLocaleString()}</span>
            {' '}{t('onboarding.profileActive').split('</2>')[1]?.split('<3>')[0]}
            <span className="text-white font-semibold">{result.agents_allocated?.length}</span>
            {' '}{t('onboarding.profileActive').split('</3>')[1]}
          </p>

          <div className="bg-[#090B0F] border border-white/10 p-4 space-y-2 text-left">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">{t('onboarding.agentsDeployed')}</div>
            {result.agents_allocated?.map(agentId => (
              <div key={agentId} className="flex items-center gap-2 py-1">
                <Bot className={`w-3.5 h-3.5 ${profile.text}`} />
                <span className="font-mono text-xs text-white">{agentId}</span>
                <span className="text-[10px] text-slate-500 ml-auto">{profile.capitalPerAgent} {t('onboarding.allocated')}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-emerald-500 text-black py-3.5 font-bold text-sm hover:bg-emerald-400 transition-colors flex items-center justify-center gap-2"
            data-testid="onboarding-go-dashboard"
          >
            {t('onboarding.goToDashboard')} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030508] text-white" data-testid="onboarding-page">
      {/* Header */}
      <div className="text-center pt-12 pb-8 px-4">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-10 h-10 bg-white flex items-center justify-center">
            <Zap className="w-6 h-6 text-[#030508]" />
          </div>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('onboarding.chooseProfile')}
        </h1>
        <p className="text-sm text-slate-400 max-w-md mx-auto">
          {t('onboarding.chooseProfileDesc')}
        </p>
      </div>

      {/* Profile Cards */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PROFILES.map(p => {
            const Icon = p.icon;
            const isSelected = selected === p.key;

            return (
              <div
                key={p.key}
                onClick={() => setSelected(p.key)}
                className={`cursor-pointer border p-5 transition-all relative ${
                  isSelected 
                    ? `${p.border} ${p.bg} ring-1 ring-${p.color}-500/30` 
                    : 'border-white/10 bg-[#090B0F] hover:border-white/20'
                }`}
                data-testid={`onboarding-profile-${p.key}`}
              >
                {isSelected && (
                  <div className={`absolute top-3 right-3 w-5 h-5 ${p.btnBg} flex items-center justify-center`}>
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 ${p.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${p.text}`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{p.name}</h3>
                    <span className={`text-[10px] ${p.text} font-mono uppercase tracking-wider`}>{p.subtitle}</span>
                  </div>
                </div>

                <p className="text-xs text-slate-400 mb-4 leading-relaxed">{p.desc}</p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-[#030508] p-2 text-center">
                    <div className="text-lg font-black text-white">{p.agents}</div>
                    <div className="text-[9px] text-slate-500 uppercase">{t('onboarding.agents')}</div>
                  </div>
                  <div className="bg-[#030508] p-2 text-center">
                    <div className="text-lg font-black text-white">{p.instruments}</div>
                    <div className="text-[9px] text-slate-500 uppercase">{t('onboarding.instruments')}</div>
                  </div>
                  <div className="bg-[#030508] p-2 text-center">
                    <div className={`text-lg font-black ${p.text}`}>{p.capitalPerAgent}</div>
                    <div className="text-[9px] text-slate-500 uppercase">{t('onboarding.perAgent')}</div>
                  </div>
                </div>

                {/* Instruments */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {p.targets.map(tgt => (
                    <span key={tgt} className={`text-[10px] font-mono px-1.5 py-0.5 ${isSelected ? `${p.bg} ${p.text}` : 'bg-white/5 text-slate-500'}`}>
                      {tgt}
                    </span>
                  ))}
                </div>

                {/* Allocation */}
                <div className="border-t border-white/5 pt-2">
                  <div className="text-[10px] text-slate-500 font-mono">{p.allocation}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirm Button */}
      <div className="max-w-md mx-auto px-4 pb-16">
        <button
          onClick={handleConfirm}
          disabled={!selected || loading}
          className={`w-full py-3.5 font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-30 ${
            selected 
              ? 'bg-emerald-500 text-black hover:bg-emerald-400' 
              : 'bg-white/10 text-white/50 cursor-not-allowed'
          }`}
          data-testid="onboarding-confirm-btn"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {selected ? t('onboarding.deployAgents', { profile: PROFILES.find(p => p.key === selected)?.name.toUpperCase() }) : t('onboarding.selectProfile')}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full mt-3 py-2 text-slate-500 hover:text-white text-xs font-mono transition-colors text-center"
          data-testid="onboarding-skip-btn"
        >
          {t('onboarding.skipForNow')}
        </button>
      </div>
    </div>
  );
}
